import { useState, useEffect, useRef, useCallback } from 'react';

interface SettingsProps {
  serverUrl: string;
  isLive?: boolean;
  onNameChange?: (name: string) => void;
  onVoiceNameChange?: (voiceName: string) => void;
}

type PersonaVoice = string;
type SettingsTab = 'basics' | 'voice' | 'advanced';

interface PersonaSettings {
  name: string;
  streamerName: string;
  voiceName: PersonaVoice;
  voiceStyle: string;
  voiceAccent: string;
  personality: string;
  eventResponseStyle: 'witty' | 'thankful' | 'hype' | 'roast' | 'custom';
  eventResponseStyleCustom: string;
  responseLength: 'brief' | 'normal' | 'detailed';
  chattiness: 'minimal' | 'moderate' | 'chatty';
  voiceGuidance: string;
  avoidTopics: string;
  customInstructions: string;
  visionInterjectionEnabled: boolean;
}

// Event Response Style options with descriptions
const EVENT_RESPONSE_STYLE_OPTIONS = [
  { value: 'witty', label: 'Witty', desc: 'Thankful + playful jokes' },
  { value: 'thankful', label: 'Thankful', desc: 'Warm and sincere' },
  { value: 'hype', label: 'Hype', desc: 'Super excited energy' },
  { value: 'roast', label: 'Roast', desc: 'Playful teasing' },
  { value: 'custom', label: 'Custom', desc: 'Write your own' },
] as const;

// Helper to get CSRF token from cookie
function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

// Built-in voice options (curated for ease of use)
const VOICE_OPTIONS: { value: string; label: string; gender: string; description: string }[] = [
  // Male voices (left column, overflow to top-right)
  { value: 'Charon', label: 'Charon', gender: 'Male', description: 'Warm, calm' },
  { value: 'Fenrir', label: 'Fenrir', gender: 'Male', description: 'Excitable, energetic' },
  { value: 'Puck', label: 'Puck', gender: 'Male', description: 'Upbeat, playful' },
  { value: 'Orus', label: 'Orus', gender: 'Male', description: 'Deep, authoritative' },
  { value: 'Zephyr', label: 'Zephyr', gender: 'Male', description: 'Breezy, light' },
  { value: 'Achird', label: 'Achird', gender: 'Male', description: 'Warm, friendly' },
  { value: 'Gacrux', label: 'Gacrux', gender: 'Male', description: 'Rich, resonant' },
  { value: 'Umbriel', label: 'Umbriel', gender: 'Male', description: 'Mysterious, soft' },
  // Female voices (right column after male overflow)
  { value: 'Kore', label: 'Kore', gender: 'Female', description: 'Firm, direct' },
  { value: 'Aoede', label: 'Aoede', gender: 'Female', description: 'Bright, expressive' },
  { value: 'Leda', label: 'Leda', gender: 'Female', description: 'Gentle, elegant' },
  { value: 'Achernar', label: 'Achernar', gender: 'Female', description: 'Bright, cheerful' },
  { value: 'Despina', label: 'Despina', gender: 'Female', description: 'Sweet, melodic' },
  { value: 'Sulafat', label: 'Sulafat', gender: 'Female', description: 'Clear, professional' },
];

export function Settings({ serverUrl, isLive = false, onNameChange, onVoiceNameChange }: SettingsProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('voice');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pendingRestart, setPendingRestart] = useState(false);
  const [settings, setSettings] = useState<PersonaSettings>({
    name: 'Buddy',
    streamerName: '',
    voiceName: 'Charon',
    voiceStyle: 'casual, calm, and conversational',
    voiceAccent: 'Australian',
    personality: 'deadpan humor, dry wit, friendly but unobtrusive',
    eventResponseStyle: 'witty',
    eventResponseStyleCustom: '',
    responseLength: 'brief',
    chattiness: 'minimal',
    voiceGuidance: '',
    avoidTopics: '',
    customInstructions: '',
    visionInterjectionEnabled: false,
  });

  // Track if initial load is complete to avoid saving on mount
  const initialLoadComplete = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Fetch persona settings
    fetch(`${serverUrl}/api/settings/persona`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; persona?: Record<string, unknown> }) => {
        if (data && !data.error) {
          const persona = data.persona ?? {};
          setSettings({
            name: (persona.name as string) || 'Buddy',
            streamerName: (persona.streamerName as string) || '',
            voiceName: (persona.voiceName as string) || 'Charon',
            voiceStyle: (persona.voiceStyle as string) || 'casual, calm, and conversational',
            voiceAccent: (persona.voiceAccent as string) || 'Australian',
            personality: (persona.personality as string) || 'deadpan humor, dry wit, friendly but unobtrusive',
            eventResponseStyle: (persona.eventResponseStyle as PersonaSettings['eventResponseStyle']) || 'witty',
            eventResponseStyleCustom: (persona.eventResponseStyleCustom as string) || '',
            responseLength: (persona.responseLength as PersonaSettings['responseLength']) || 'brief',
            chattiness: (persona.chattiness as PersonaSettings['chattiness']) || 'minimal',
            voiceGuidance: (persona.voiceGuidance as string) || '',
            avoidTopics: Array.isArray(persona.avoidTopics) ? (persona.avoidTopics as string[]).join(', ') : ((persona.avoidTopics as string) || ''),
            customInstructions: (persona.customInstructions as string) || '',
            visionInterjectionEnabled: (persona.visionInterjectionEnabled as boolean) || false,
          });
          onNameChange?.((persona.name as string) || 'Buddy');
          onVoiceNameChange?.((persona.voiceName as string) || 'Charon');
        }
        initialLoadComplete.current = true;
      })
      .catch(() => {
        // Use defaults
        onNameChange?.('Buddy');
        initialLoadComplete.current = true;
      });
  }, [onNameChange, onVoiceNameChange, serverUrl]);

  const saveSettings = useCallback(async (newSettings: PersonaSettings) => {
    setSaveStatus('saving');
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const payload = {
        ...newSettings,
        avoidTopics: newSettings.avoidTopics
          ? newSettings.avoidTopics
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t && !['none', 'n/a', 'na', 'nothing', ''].includes(t.toLowerCase()))
          : [],
      };

      const res = await fetch(`${serverUrl}/api/settings/persona`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSaveStatus('saved');
        setPendingRestart(true);
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [serverUrl]);

  // Debounced auto-save on settings change
  const updateSetting = useCallback(<K extends keyof PersonaSettings>(key: K, value: PersonaSettings[K]) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    if (key === 'name') {
      onNameChange?.(String(value).trim() || 'Buddy');
    }
    if (key === 'voiceName') {
      onVoiceNameChange?.(String(value).trim() || 'Charon');
    }

    if (!initialLoadComplete.current) return;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      void saveSettings(newSettings);
    }, 500);
  }, [onNameChange, onVoiceNameChange, settings, saveSettings]);

  const tabs: { id: SettingsTab; label: string; desc: string }[] = [
    { id: 'voice', label: 'Voice', desc: 'Sound' },
    { id: 'basics', label: 'Basics', desc: 'Persona' },
    { id: 'advanced', label: 'Advanced', desc: 'Rules' },
  ];
  const selectedVoice = VOICE_OPTIONS.find((voice) => voice.value === settings.voiceName) ?? VOICE_OPTIONS[0];

  return (
    <div className="space-y-3">
      <div className="relative overflow-hidden rounded-[22px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.92),rgba(18,16,39,0.86))] p-3 lg:p-4">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_0%,rgba(168,85,247,0.18),transparent_32%),radial-gradient(circle_at_90%_35%,rgba(34,211,238,0.11),transparent_30%)]" />
        <div className="relative flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-white/[0.35]">Voice Studio</div>
            <div className="mt-2 flex flex-wrap items-end gap-x-3 gap-y-1">
              <h3 className="text-xl font-black tracking-[-0.035em] text-white lg:text-2xl">Tune {settings.name || 'Buddy'}'s voice</h3>
              <span className="pb-1 text-xs font-bold text-cyan-100/[0.58]">{settings.name} / {selectedVoice.label}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {saveStatus !== 'idle' && (
              <div className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] transition-all ${saveStatus === 'saving'
                ? 'border-cyan-300/20 bg-cyan-400/10 text-cyan-200 animate-pulse'
                : saveStatus === 'saved'
                  ? 'border-emerald-300/20 bg-emerald-400/10 text-emerald-200'
                  : 'border-red-300/20 bg-red-400/10 text-red-200'
                }`}>
                {saveStatus === 'saving' ? 'Syncing...' : saveStatus === 'saved' ? 'Synced to Cloud' : 'Sync Error'}
              </div>
            )}
            <div className="flex rounded-[18px] border border-white/10 bg-black/[0.24] p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`min-w-[82px] rounded-[14px] px-3 py-2 text-left transition-all lg:min-w-[96px] ${activeTab === tab.id
                    ? 'bg-[linear-gradient(135deg,rgba(192,38,211,0.75),rgba(37,99,235,0.74))] text-white shadow-[0_12px_30px_rgba(124,58,237,0.24)]'
                    : 'text-slate-300/[0.56] hover:bg-white/[0.055] hover:text-white'
                    }`}
                >
                  <div className="text-xs font-black">{tab.label}</div>
                  <div className="mt-0.5 text-[9px] font-black uppercase tracking-[0.18em] opacity-60">{tab.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isLive && pendingRestart && (
        <div className="flex items-center gap-3 rounded-[18px] border border-amber-300/[0.24] bg-amber-400/10 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-amber-300/[0.22] bg-amber-400/10 text-amber-200">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-amber-100">Settings Changed</p>
            <p className="text-xs text-amber-100/[0.65]">Restart the AI session to apply your changes.</p>
          </div>
          <button
            type="button"
            onClick={() => setPendingRestart(false)}
            className="rounded-lg px-2 py-1 text-amber-100/50 transition-colors hover:bg-white/[0.06] hover:text-amber-100"
            title="Dismiss"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {activeTab === 'voice' && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[280px_minmax(0,1fr)] 2xl:grid-cols-[320px_minmax(0,1fr)]">
          <section className="rounded-[24px] border border-white/10 bg-[linear-gradient(145deg,rgba(15,23,42,0.82),rgba(12,18,32,0.9))] p-4">
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-100/[0.45]">Vocal Synthesis</div>
            <div className="relative mt-3 overflow-hidden rounded-[20px] border border-cyan-200/20 bg-[linear-gradient(145deg,rgba(34,211,238,0.11),rgba(168,85,247,0.12),rgba(2,6,23,0.5))] p-3 lg:p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_18px_46px_rgba(8,47,73,0.18)]">
              <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-cyan-300/20 blur-3xl" />
              <div className="absolute -bottom-12 left-6 h-24 w-24 rounded-full bg-fuchsia-400/[0.18] blur-3xl" />
              <div className="relative flex items-center gap-3">
                <div className="relative flex h-12 w-12 items-center justify-center rounded-full border border-white/30 bg-black/[0.22] text-base font-black text-white shadow-[0_0_34px_rgba(34,211,238,0.18)] lg:h-14 lg:w-14">
                  <div className="absolute inset-1 rounded-full border border-cyan-200/20" />
                  {selectedVoice.label.slice(0, 1)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xl font-black tracking-[-0.03em] text-white lg:text-2xl">{selectedVoice.label}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-300/[0.62]">
                    <span>{selectedVoice.description}</span>
                    <span className="h-1 w-1 rounded-full bg-cyan-200/50" />
                    <span>{selectedVoice.gender}</span>
                  </div>
                </div>
              </div>
              <div className="relative mt-4 rounded-[16px] border border-white/10 bg-black/[0.18] px-3 pb-2.5 pt-2">
                <div className="relative z-10 text-[9px] font-black uppercase tracking-[0.2em] text-cyan-100/40">Voice signature</div>
                <div className="relative mt-1 flex h-8 items-center justify-center gap-1.5 lg:h-9">
                  <div className="absolute left-3 right-3 top-1/2 h-px bg-gradient-to-r from-transparent via-cyan-200/35 to-transparent" />
                  {Array.from({ length: 26 }).map((_, index) => (
                    <span
                      key={index}
                      className="relative w-1.5 rounded-full bg-gradient-to-t from-cyan-300 via-blue-400 to-fuchsia-400 shadow-[0_0_10px_rgba(34,211,238,0.24)]"
                      style={{ height: `${8 + ((index * 17) % 26)}px`, opacity: 0.45 + ((index % 4) * 0.08) }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 space-y-2.5">
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/50">Accent & Nuance</span>
                <input
                  type="text"
                  value={settings.voiceAccent}
                  onChange={(e) => updateSetting('voiceAccent', e.target.value)}
                  className="mt-2 w-full rounded-[16px] border border-white/10 bg-black/[0.26] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-300/[0.35]"
                  placeholder="neutral American English"
                />
              </label>

              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/50">Cadence & Tempo</span>
                <input
                  type="text"
                  value={settings.voiceStyle}
                  onChange={(e) => updateSetting('voiceStyle', e.target.value)}
                  className="mt-2 w-full rounded-[16px] border border-white/10 bg-black/[0.26] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-300/[0.35]"
                  placeholder="casual and conversational"
                />
              </label>
            </div>
          </section>

          <section className="rounded-[24px] border border-white/10 bg-black/[0.18] p-3 lg:p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-black text-white">Studio Voice Library</h3>
                <p className="mt-1 text-xs text-slate-300/56">Choose the voice your audience will hear.</p>
              </div>
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white/[0.45]">{VOICE_OPTIONS.length} voices</span>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 2xl:grid-cols-3">
              {VOICE_OPTIONS.map((voice) => (
                <button
                  key={voice.value}
                  type="button"
                  onClick={() => updateSetting('voiceName', voice.value)}
                  className={`group rounded-[18px] border p-3 text-left transition-all hover:-translate-y-0.5 ${settings.voiceName === voice.value
                    ? 'border-cyan-300/30 bg-[linear-gradient(135deg,rgba(34,211,238,0.14),rgba(168,85,247,0.12))] shadow-[0_16px_38px_rgba(34,211,238,0.08)]'
                    : 'border-white/10 bg-white/[0.035] hover:border-white/20 hover:bg-white/[0.06]'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-xl border text-xs font-black ${voice.gender === 'Female'
                      ? 'border-fuchsia-300/[0.24] bg-fuchsia-500/10 text-fuchsia-100'
                      : 'border-cyan-300/20 bg-cyan-400/10 text-cyan-100'
                      }`}>
                      {voice.label.slice(0, 1)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-black text-white">{voice.label}</div>
                      <div className="mt-0.5 truncate text-[11px] text-slate-300/[0.56]">{voice.description}</div>
                    </div>
                    {settings.voiceName === voice.value && <span className="h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.7)]" />}
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}

      {activeTab === 'basics' && (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
          <section className="rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.88),rgba(18,16,39,0.82))] p-4">
            <div className="mb-4">
              <h3 className="text-lg font-black text-white">AI Persona Basics</h3>
              <p className="mt-1 text-sm text-slate-300/[0.62]">Set the identity, tone, and response style StreamBuddy uses on stream.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block rounded-[18px] border border-white/10 bg-black/[0.18] p-3">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-100/50">AI Identity</span>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => updateSetting('name', e.target.value)}
                  className="mt-2 w-full rounded-[14px] border border-white/10 bg-black/[0.28] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-fuchsia-300/[0.35]"
                  placeholder="Buddy"
                />
              </label>
              <label className="block rounded-[18px] border border-white/10 bg-black/[0.18] p-3">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-100/50">Streamer Handle</span>
                <input
                  type="text"
                  value={settings.streamerName}
                  onChange={(e) => updateSetting('streamerName', e.target.value)}
                  className="mt-2 w-full rounded-[14px] border border-white/10 bg-black/[0.28] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-fuchsia-300/[0.35]"
                  placeholder="Your name or channel name"
                />
              </label>
            </div>

            <label className="mt-3 block rounded-[18px] border border-white/10 bg-black/[0.18] p-3">
              <span className="text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-100/50">Core Personality & Temperament</span>
              <textarea
                value={settings.personality}
                onChange={(e) => updateSetting('personality', e.target.value)}
                rows={3}
                className="mt-2 w-full resize-none rounded-[14px] border border-white/10 bg-black/[0.28] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-fuchsia-300/[0.35]"
                placeholder="friendly, helpful, and witty"
              />
            </label>

            <div className="mt-3 rounded-[18px] border border-white/10 bg-black/[0.18] p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-100/50">Alert Reaction Style</span>
                <span className="text-[10px] text-slate-300/[0.45]">Donations, subs, and raids</span>
              </div>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
                {EVENT_RESPONSE_STYLE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateSetting('eventResponseStyle', option.value)}
                    className={`rounded-[14px] border px-3 py-2 text-left transition-all ${settings.eventResponseStyle === option.value
                      ? 'border-fuchsia-300/30 bg-fuchsia-500/[0.14] text-white'
                      : 'border-white/10 bg-white/[0.035] text-slate-300/[0.68] hover:bg-white/[0.06]'
                      }`}
                  >
                    <div className="text-xs font-black">{option.label}</div>
                    <div className="mt-0.5 text-[10px] text-slate-300/50">{option.desc}</div>
                  </button>
                ))}
              </div>
              {settings.eventResponseStyle === 'custom' && (
                <textarea
                  value={settings.eventResponseStyleCustom}
                  onChange={(e) => updateSetting('eventResponseStyleCustom', e.target.value)}
                  rows={2}
                  className="mt-3 w-full resize-none rounded-[14px] border border-white/10 bg-black/[0.28] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-fuchsia-300/[0.35]"
                  placeholder="Describe exactly how the AI should react to events..."
                />
              )}
            </div>
          </section>

          <aside className="space-y-3">
            <section className="rounded-[24px] border border-white/10 bg-black/[0.18] p-4">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/[0.35]">Response Depth</div>
              <div className="mt-3 space-y-2">
                {[
                  { value: 'brief', label: 'Brief', desc: 'Punchy 1-2 sentences' },
                  { value: 'normal', label: 'Balanced', desc: 'Standard 2-4 sentences' },
                  { value: 'detailed', label: 'Storyteller', desc: 'Rich, multi-paragraph' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateSetting('responseLength', option.value as 'brief' | 'normal' | 'detailed')}
                    className={`w-full rounded-[16px] border px-3 py-3 text-left transition-all ${settings.responseLength === option.value
                      ? 'border-cyan-300/[0.26] bg-cyan-400/10 text-white'
                      : 'border-white/10 bg-white/[0.035] text-slate-300/[0.68] hover:bg-white/[0.06]'
                      }`}
                  >
                    <div className="text-sm font-black">{option.label}</div>
                    <div className="mt-0.5 text-xs text-slate-300/[0.52]">{option.desc}</div>
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(168,85,247,0.08))] p-4">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/[0.35]">Current Mix</div>
              <div className="mt-3 text-sm font-black text-white">{settings.name || 'Buddy'} / {selectedVoice.label}</div>
              <div className="mt-1 text-xs leading-5 text-slate-300/[0.62]">{settings.personality || 'No personality set yet.'}</div>
            </section>
          </aside>
        </div>
      )}

      {activeTab === 'advanced' && (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_320px]">
          <section className="rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.9),rgba(30,20,36,0.78))] p-4">
            <div className="mb-4">
              <h3 className="text-lg font-black text-white">Advanced Behavioral Constraints</h3>
              <p className="mt-1 text-sm text-slate-300/[0.62]">Optional rules for edge cases, restricted topics, and exact delivery notes.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block rounded-[18px] border border-white/10 bg-black/[0.18] p-3">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-100/[0.55]">Synthesis Guidance</span>
                <input
                  type="text"
                  value={settings.voiceGuidance}
                  onChange={(e) => updateSetting('voiceGuidance', e.target.value)}
                  className="mt-2 w-full rounded-[14px] border border-white/10 bg-black/[0.28] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-300/[0.35]"
                  placeholder="optional"
                />
                <p className="mt-2 text-[10px] text-slate-300/[0.45]">Example: "Laugh occasionally", "Whisper when being secretive"</p>
              </label>

              <label className="block rounded-[18px] border border-white/10 bg-black/[0.18] p-3">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-100/[0.55]">Global Topic Filter</span>
                <input
                  type="text"
                  value={settings.avoidTopics}
                  onChange={(e) => updateSetting('avoidTopics', e.target.value)}
                  className="mt-2 w-full rounded-[14px] border border-white/10 bg-black/[0.28] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-300/[0.35]"
                  placeholder="politics, religion, competitor games"
                />
                <p className="mt-2 text-[10px] text-slate-300/[0.45]">Comma-separated list of topics the AI should never discuss.</p>
              </label>
            </div>

            <label className="mt-3 block rounded-[18px] border border-white/10 bg-black/[0.18] p-3">
              <span className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-100/[0.55]">Hard Behavioral Rules</span>
              <textarea
                value={settings.customInstructions}
                onChange={(e) => updateSetting('customInstructions', e.target.value)}
                rows={5}
                className="mt-2 w-full resize-none rounded-[14px] border border-white/10 bg-black/[0.28] px-4 py-3 font-mono text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-300/[0.35]"
                placeholder="Example: Always hype up subscribers, Never spoil game endings..."
              />
            </label>
          </section>

          <aside className="rounded-[24px] border border-amber-300/20 bg-amber-400/[0.08] p-4">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-100/[0.55]">Guidance</div>
            <h4 className="mt-2 text-base font-black text-white">Keep strict rules short</h4>
            <p className="mt-2 text-sm leading-6 text-slate-300/[0.65]">
              Defaults should handle most streams. Use advanced rules for boundaries that need to be consistent every time.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
